import { useContext, useMemo } from 'react';
import { Controller, get, set, type Control, type ControllerProps } from 'react-hook-form';
import { formUnregistersFields, readDefaultValues, withDefaultValues } from '@srm/ui/lib/rhfBridge';
import { DocumentValidationIndexContext } from '@/context/Contexts';

/**
 * `control` для одного поля: `_defaultValues` — только значение этого поля, остальное — от формы (прототипом).
 *
 * RHF 7.91: `useFormState` внутри `Controller` на монтировании и размонтировании клонирует состояние формы вместе с
 * `defaultValues` целиком (`useResyncOnReconnect`). На документе с таблицей в тысячи строк это десятки миллисекунд
 * на каждое поле при переходе в правку, первом открытии вкладки и выходе из правки. Полю нужны только свои
 * значения по умолчанию: `defaultValue` поля и `formState.defaultValues`, который читает рендер-проп.
 * Методы `control` RHF — замыкания, поля состояния — геттеры: прототипная обёртка видит ту же форму.
 */
const fieldScopedControl = (control: Control, name: string): Control =>
    withDefaultValues(control, () => {
        const scoped = {};
        const value: unknown = get(readDefaultValues(control), name);
        if (value !== undefined) set(scoped, name, value);
        return scoped;
    });

const DocumentFormController = (props: ControllerProps) => {
    const validationIndex = useContext(DocumentValidationIndexContext);
    // control из пропа: поля документа передают его всегда; без него Controller берёт форму из контекста как есть
    const formControl = props.control as Control | undefined;
    const dependents = useMemo(
        () => validationIndex?.getDependents(String(props.name)) ?? [],
        [props.name, validationIndex],
    );

    const rules = useMemo(() => {
        if (dependents.length === 0) return props.rules;

        const configuredDeps = props.rules?.deps;
        const existingDeps =
            configuredDeps == null ? [] : Array.isArray(configuredDeps) ? configuredDeps : [configuredDeps];
        const deps = Array.from(new Set([...existingDeps.map(String), ...dependents]));
        return { ...props.rules, deps };
    }, [dependents, props.rules]);

    // shouldUnregister пишет значение по умолчанию поля в `_defaultValues` формы — такому полю нужна сама форма
    const unregisters = props.shouldUnregister || (formControl && formUnregistersFields(formControl));
    const control = useMemo(
        () => (formControl && !unregisters ? fieldScopedControl(formControl, String(props.name)) : formControl),
        [formControl, unregisters, props.name],
    );

    return <Controller {...props} control={control as ControllerProps['control']} rules={rules} />;
};

export default DocumentFormController;
